import React, { useState, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import SearchForm from "../components/search-form";
import { PageLayout } from "../components/page-layout";
import {
  Box,
  Typography,
  Paper,
  Checkbox,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import Papa from "papaparse";
 
const CSV_URL =
  "https://finance-files-servless-fabio.s3.us-east-1.amazonaws.com/listed_stocks.csv";

export default function SearchPage() {
  const { user } = useAuth0();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [allStocks, setAllStocks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [savedTickers, setSavedTickers] = useState([]);
  const [message, setMessage] = useState("");

  const storageKey = `portfolio_${user?.email}`;

  /* ---------------- LOAD PORTFOLIO ---------------- */
  useEffect(() => {
    if (!user?.email) return;
    const saved = localStorage.getItem(storageKey);
    if (saved) {
      setSavedTickers(JSON.parse(saved));
    }
  }, [storageKey, user?.email]);



  async function executeSearch(q) {
  setError("");
  setResults([]);

  if (!q.trim()) {
    setError("Type a term to search.");
    return;
  }

  setLoading(true);

  try {
    const CSV_URL =
      "https://finance-files-servless-fabio.s3.us-east-1.amazonaws.com/listed_stocks.csv";

    // Faz o download do CSV
    const response = await fetch(CSV_URL);
    if (!response.ok) {
      throw new Error(`Error loading CSV - ${response.status}`);
    }

    const csvText = await response.text();

    // Converte CSV para JSON
    const parsed = Papa.parse(csvText, {
      header: true,
      skipEmptyLines: true,
    });

    const allStocks = parsed.data;

    // Filtra ticker OU name
    const filtered = allStocks.filter((stock) => {
      const ticker = stock.ticker?.toLowerCase() || "";
      const name = stock.name?.toLowerCase() || "";
      const search = q.toLowerCase();

      return ticker.includes(search) || name.includes(search);
    });

    // Limita resultados (ex: 20)
    setResults(filtered.slice(0, 2000));
  } catch (err) {
    setError(err?.message || "Error when searching.");
  } finally {
    setLoading(false);
  }
}

  function handleSubmit(e) {
    e.preventDefault();
    executeSearch(query);
    setSubmitted(true);
  }

  /* ---------------- TOGGLE PORTFOLIO ---------------- */
  const handleToggleTicker = (ticker) => {
    let updated;
    let msg = "";
    if (savedTickers.includes(ticker)) {
      updated = savedTickers.filter((t) => t !== ticker);
      msg = "Removed from Portfolio";
    } else {
      updated = [...savedTickers, ticker];
      msg = "Added to Portfolio";
    }
    setSavedTickers(updated);
    localStorage.setItem(storageKey, JSON.stringify(updated));

    setMessage(msg);
    setTimeout(() => setMessage(""), 2000);
  };

  if (!user) {
    return null;
  }

  return (
    <PageLayout>
      <Typography variant="h4" gutterBottom>
        Search Stocks
      </Typography>

      <SearchForm
        query={query}
        onQueryChange={setQuery}
        onSubmit={handleSubmit}
        loading={loading}
      />

      {error && (
        <Typography color="error" sx={{ mt: 2 }}>
          {error}
        </Typography>
      )}

      {submitted && !loading && !error && results.length === 0 && query && (
        <Typography sx={{ mt: 2 }}>
          Search not found “{query}”.
        </Typography>
      )}

      {message && (
        <Typography color="primary" sx={{ mt: 2 }}>
          {message}
        </Typography>
      )}

      {results.length > 0 && (
        <Box mt={4}>
          <Paper elevation={2}>
            <Box p={2}>
              {results.map((row) => (
                <Box
                  key={row.ticker}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    borderBottom: "1px solid #333",
                    py: 0.5,
                    px: 1,
                  }}
                >
                  {/* Checkbox */}
                  <Checkbox
                    checked={savedTickers.includes(row.ticker)}
                    onChange={() => handleToggleTicker(row.ticker)}
                  />

                  {/* Info: ticker / name / exchange */}
                  <Box
                    component={RouterLink}
                    to={`/stock/${row.ticker}`}
                    sx={{
                      flex: 1,
                      display: "flex",
                      justifyContent: "space-between",
                      textDecoration: "none",
                      color: "inherit",
                      "&:hover": {
                        backgroundColor: "rgba(255,255,255,0.05)",
                      },
                      px: 1,
                      py: 0.5,
                    }}
                  >
                    <Typography fontWeight={600}>{row.ticker}</Typography>
                    <Typography sx={{ ml: 2, color: "text.secondary" }}>
                      {row.name}
                    </Typography>
                    <Typography sx={{ ml: 2, color: "text.secondary", fontStyle: "italic" }}>
                      {row.exchange}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Paper>
        </Box>
      )}
    </PageLayout>
  );
}
